import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { RecoveryLocation } from '../../api'
import { ClockDial, type ClockFace } from '@/components/clock/ClockDial'
import { coordinatesOf, skyPhase, type SkyPhase } from '@/lib/sun'
import dawnSky from '@/assets/sky/dawn.webp'
import daySky from '@/assets/sky/day.webp'
import duskSky from '@/assets/sky/dusk.webp'
import nightSky from '@/assets/sky/night.webp'

export type { ClockFace }

export interface ClockSettings {
    face: ClockFace
    showSeconds: boolean
    showAnalog: boolean
}

/**
 * IANA/DST-safe zoned time. Uses Intl with the location's IANA timezone so DST is
 * automatic — never a fixed UTC offset. (en-GB/24h purely to parse numeric parts.)
 */
function getZonedTime(timeZone: string) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone || 'UTC',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date())
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
    let hour = get('hour')
    if (hour === 24) hour = 0 // en-GB reports midnight as 24
    return { hour, minute: get('minute'), second: get('second') }
}

/**
 * The sky behind each clock: dawn, day, dusk or night AT THAT SITE, now.
 *
 * Andrew, 2026-10-01: "use these as background for the card that each clock is on ... Determine the
 * times of day in each location of which image to display." Worked out from where the sun is over
 * the site (lib/sun), so it follows the real sunrise and sunset there through the year. A location
 * with no coordinates has no known sky and keeps the plain card: a guess from the clock's hour
 * would be wrong for most of the year and would look exactly as sure of itself as the real thing.
 */
const SKY: Record<SkyPhase, string> = { dawn: dawnSky, day: daySky, dusk: duskSky, night: nightSky }
/**
 * How each picture sits in the card. Day and night fill it as they are: their sun and moon are
 * already off to one side. At dawn and dusk the sun is in the middle of the picture, exactly where
 * the dial is, so those two are drawn larger and moved until the sun stands beside the dial on the
 * horizon - it is what makes the two recognisable at a glance.
 */
const SKY_FRAME: Record<SkyPhase, { backgroundSize: string; backgroundPosition: string }> = {
    dawn: { backgroundSize: '170% auto', backgroundPosition: '4% 100%' },
    day: { backgroundSize: 'cover', backgroundPosition: 'center' },
    dusk: { backgroundSize: '170% auto', backgroundPosition: '4% 79%' },
    night: { backgroundSize: 'cover', backgroundPosition: 'center' },
}
const SKY_NAMES: Record<SkyPhase, string> = { dawn: 'Dawn', day: 'Daytime', dusk: 'Dusk', night: 'Night' }

/** A clock + digital readout + two summary tiles for one location. */
export function LocationClock({ location, settings }: { location: RecoveryLocation; settings: ClockSettings }) {
    const { t, i18n } = useTranslation()
    const { face, showSeconds, showAnalog } = settings
    const [, setTick] = useState(0)
    useEffect(() => {
        const id = setInterval(() => setTick((n) => (n + 1) % 86400), 1000)
        return () => clearInterval(id)
    }, [])

    const { hour, minute, second } = getZonedTime(location.timeZone)

    // Digital readout — 12h/24h + digit formatting follow the user's locale.
    let digital: string
    try {
        digital = new Intl.DateTimeFormat(i18n.language || undefined, {
            timeZone: location.timeZone || 'UTC',
            hour: '2-digit',
            minute: '2-digit',
            ...(showSeconds ? { second: '2-digit' as const } : {}),
        }).format(new Date())
    } catch {
        const p = (n: number) => String(n).padStart(2, '0')
        digital = showSeconds ? `${p(hour)}:${p(minute)}:${p(second)}` : `${p(hour)}:${p(minute)}`
    }

    const pad = (n: number) => String(n).padStart(2, '0')
    const timeParts = showSeconds ? [pad(hour), pad(minute), pad(second)] : [pad(hour), pad(minute)]

    const nf = new Intl.NumberFormat(i18n.language || undefined)

    const position = coordinatesOf(location.latitude, location.longitude)
    const sky = position ? skyPhase(new Date(), position.latitude, position.longitude) : null
    const skyLabel = sky ? t(`recoverRequests.locationTracking.sky.${sky}`, { defaultValue: SKY_NAMES[sky] }) : undefined

    return (
        <div className="flex w-72 shrink-0 flex-col items-center gap-1.5">
            <div className="w-full truncate text-center text-sm font-semibold text-foreground" title={location.name}>
                {location.name}
            </div>

            {showAnalog ? (
                <div
                    className="relative w-full overflow-hidden rounded-xl border border-black/10 bg-card bg-no-repeat px-3 pt-4 pb-3"
                    style={sky ? { backgroundImage: `url(${SKY[sky]})`, ...SKY_FRAME[sky] } : undefined}
                    data-sky={sky ?? undefined}
                    title={skyLabel}
                    data-testid={`location-clock-card-${location.id}`}
                >
                    <div className="mx-auto w-fit">
                        <ClockDial
                            hours={hour}
                            minutes={minute}
                            seconds={showSeconds ? second : null}
                            size={96}
                            face={face}
                            ariaLabel={`${location.name} ${digital}`}
                        />
                    </div>
                </div>
            ) : null}

            <div className={`font-light tabular-nums tracking-wide text-muted-foreground ${showAnalog ? 'text-lg' : 'text-4xl py-2'}`}>
                {timeParts.join(':')}
            </div>

            <div className="grid w-full grid-cols-2 gap-1.5">
                <div className="rounded-md border bg-muted/30 px-2 py-1 text-center">
                    <div className="text-sm font-semibold text-foreground">{nf.format(location.objectCount)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {t('recoverRequests.locationTracking.objects', 'Objects')}
                    </div>
                </div>
                <div className="rounded-md border bg-muted/30 px-2 py-1 text-center">
                    <div className="text-sm font-semibold text-foreground">{nf.format(location.assignedEngineerCount)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {t('recoverRequests.locationTracking.engineers', 'Engineers')}
                    </div>
                </div>
            </div>
        </div>
    )
}
